// src/pages/WorkOrder/WorkOrderPage.jsx
'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Chip, CircularProgress, IconButton, TextField } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import AssignmentIcon from '@mui/icons-material/Assignment';
import DataTable from '../../../../components/DataTable';
import {
  useVehicleMaintenanceList,
  fetchAllVehicleMaintenance,
  getMaintenanceById,
  updateMaintenance,
  revalidateVehicleMaintenanceList
} from '../../../../stores/vehicleMaintenanceStore';
import { useCompanyList } from '../../../../stores/companyStore';
import { useNotifier } from '../../../../components/Notifier';
import DetailServiceTypeDialog from './DetailServiceTypeDialog';
import FormDialog from '../../../../components/FormDialog';
import WorkOrderAddEdit from './WorkOrderAddEdit';
import DetailWorkOrderDialog from './DetailWorkOrderDialog';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { exportExcelWorkOrder } from './ExportWorkOrder';
import { useLanguage } from '../../../../utils/i18n';

const formatDateShort = (val) => {
  if (!val) return '';
  try {
    const d = dayjs(val);
    if (!d.isValid()) return String(val).slice(0, 10);
    return d.format('YYYY-MM-DD');
  } catch {
    return String(val).slice(0, 10);
  }
};

export default function WorkOrderPage() {
  return (
    <>
      <WorkOrderPageContent />
    </>
  );
}

function WorkOrderPageContent() {
  const { t } = useLanguage();
  const notify = useNotifier();
  const formRef = useRef();

  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [searchField, setSearchField] = useState('all');
  const [searchRaw, setSearchRaw] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [statusFilter, setStatusFilter] = useState('onprogress');

  const [openDialogDetail, setOpenDialogDetail] = useState(false);
  const [detailRow, setDetailRow] = useState(null);

  const [openDialogDetailWO, setOpenDialogDetailWO] = useState(false);
  const [detailWORow, setDetailWORow] = useState(null);

  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [openDialogMarkTask, setOpenDialogMarkTask] = useState(false);
  const [markLoading, setMarkLoading] = useState(false);
  const [markSubmitting, setMarkSubmitting] = useState(false);
  const todayStr = dayjs().format('YYYY-MM-DD');
  const { companyOptions } = useCompanyList();
  const [selectedCompanies, setSelectedCompanies] = useState([]);

  const companyOptionsNormalized = useMemo(
    () =>
      (companyOptions || []).map((c) => ({
        value: String(c.value ?? c.id ?? c),
        label: c.label ?? c.name ?? String(c.value ?? c.id ?? c)
      })),
    [companyOptions]
  );

  useEffect(() => {
    if (!companyOptionsNormalized.length) return;
    const stored = localStorage.getItem('companyId');
    if (!stored) return;

    const match = companyOptionsNormalized.find((o) => String(o.value) === String(stored));
    const selected = match ? [{ value: String(match.value), label: match.label }] : [{ value: String(stored), label: String(stored) }];

    setSelectedCompanies((prev) => (prev[0]?.value === selected[0].value ? prev : selected));
  }, [companyOptionsNormalized]);

  const companyFilterColumns = useMemo(() => {
    if (selectedCompanies.length > 0) {
      const hasAll = selectedCompanies.some((s) => s.value === 'all');
      if (hasAll) return [];
      const values = selectedCompanies.map((s) => s.value);
      return [
        {
          name: 'company_id',
          logic_operator: values.length === 1 ? '=' : 'in',
          operator: 'AND',
          value: values.join(',')
        }
      ];
    }
    return [];
  }, [selectedCompanies]);

  const selectedCompanyIds = useMemo(() => {
    if (!selectedCompanies.length) return '';
    if (selectedCompanies.some((s) => s.value === 'all')) return '';
    return selectedCompanies.map((s) => s.value).join(',');
  }, [selectedCompanies]);

  const handleCompanyChange = (options = []) => {
    const sanitized = options.map((o) => ({ value: String(o.value), label: o.label }));
    setSelectedCompanies(sanitized);
    if (sanitized.length === 1) localStorage.setItem('companyId', sanitized[0].value);
    else localStorage.removeItem('companyId');
    setPaginationModel((p) => ({ ...p, page: 0 }));
  };

  const normalizeIncomingDate = (raw) => {
    if (!raw) return null;
    const s = String(raw).trim();
    const datePart = s.includes('T') ? s.split('T')[0] : s;
    if (!datePart) return null;
    if (datePart.startsWith('0001') || datePart.startsWith('1900') || datePart.startsWith('0000') || datePart === '0000-00-00') return null;
    const dd = dayjs(datePart);
    return dd.isValid() ? dd.format('YYYY-MM-DD') : null;
  };

  const [markValues, setMarkValues] = useState({ actual_odometer: '', date_completed_actual: todayStr });

  const searchFieldOptions = useMemo(
    () => [
      { value: 'vm_number', label: t('workOrder.woNumber', 'WO Number') },
      { value: 'license_plate', label: t('workOrder.plateVehicle', 'Plate Vehicle') },
      { value: 'vm_date', label: t('workOrder.woDate', 'WO Date') },
      { value: 'date_completed_actual', label: t('workOrder.actualDate', 'Actual Date') },
      { value: 'workshop', label: t('workOrder.workshop', 'Workshop') },
      { value: 'price', label: t('workOrder.price', 'Price') }
    ],
    [t]
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchValue(String(searchRaw ?? '').trim());
      setPaginationModel((p) => ({ ...p, page: 0 }));
    }, 500);
    return () => clearTimeout(timer);
  }, [searchRaw, searchField]);

  const {
    data: serverRows,
    totalRecord: hookTotal,
    isLoading: hookLoading
  } = useVehicleMaintenanceList(
    paginationModel.page,
    paginationModel.pageSize,
    searchField,
    searchValue,
    statusFilter,
    selectedCompanyIds,
    companyFilterColumns
  );

  const rows = serverRows;
  const isLoading = hookLoading;
  const totalRecords = Number(hookTotal ?? 0);

  const handleFetchAllForExport = async () => {
    try {
      return await fetchAllVehicleMaintenance({
        searchKey: searchField,
        searchValue,
        statusFilter,
        companyFilterColumns,
        selectedCompanyIds,
        total: totalRecords
      });
    } catch (err) {
      console.error('work order export all error', err);
      notify({ message: t('workOrder.failedExport', 'Failed to fetch data for export'), severity: 'error' });
      return rows;
    }
  };

  const handlePageChange = (model) => {
    setPaginationModel((prev) => {
      if (prev.page === model.page && prev.pageSize === model.pageSize) return prev;
      return model;
    });
  };

  const handleSearch = ({ field, value }) => {
    if (field === 'status') {
      setStatusFilter(value || '');
      setPaginationModel((p) => ({ ...p, page: 0 }));
      return;
    }
    setSearchField(field);
    setSearchRaw(value ?? '');
    setPaginationModel((p) => ({ ...p, page: 0 }));
  };

  const handleShowDetail = (row) => {
    if (!row) {
      notify({ message: t('workOrder.dataNotFound', 'Data not found'), severity: 'error' });
      return;
    }
    setDetailRow(row);
    setOpenDialogDetail(true);
  };
  const handleShowDetailWO = (row) => {
    if (!row) {
      notify({ message: t('workOrder.dataNotFound', 'Data not found'), severity: 'error' });
      return;
    }
    setDetailWORow(row);
    setOpenDialogDetailWO(true);
  };
  const handleAddClick = () => {
    setEditingId(null);
    setOpenDialog(true);
  };

  const handleEditClick = (id) => {
    setEditingId(id);
    setOpenDialog(true);
  };

  const handleMarkTask = async (id) => {
    setEditingId(id);
    setOpenDialogMarkTask(true);
    setMarkLoading(true);
    try {
      const data = await getMaintenanceById(id);
      const actualOdo = data?.actual_odometer ?? data?.current_odometer ?? '';
      const normalized = normalizeIncomingDate(data?.date_completed_actual);
      setMarkValues({
        actual_odometer: actualOdo,
        date_completed_actual: normalized || todayStr
      });
    } catch (err) {
      console.error('mark task load failed', err);
      notify({ message: t('workOrder.failedLoadMarkTask', 'Failed to load data for mark task'), severity: 'error' });
      setOpenDialogMarkTask(false);
    } finally {
      setMarkLoading(false);
    }
  };

  const handleCloseMarkTask = () => {
    setOpenDialogMarkTask(false);
    setEditingId(null);
    setMarkValues({ actual_odometer: '', date_completed_actual: '' });
    setMarkLoading(false);
    setMarkSubmitting(false);
  };

  const handleSaveMarkTask = async () => {
    if (!editingId) return;
    setMarkSubmitting(true);
    try {
      const payload = {
        id: editingId,
        status: 'completed',
        actual_odometer: markValues.actual_odometer ? Number(markValues.actual_odometer) : null,
        date_completed_actual: formatDateShort(markValues.date_completed_actual)
          ? `${formatDateShort(markValues.date_completed_actual)}T00:00:00Z`
          : null
      };

      await updateMaintenance(editingId, payload);
      revalidateVehicleMaintenanceList();

      notify({
        message: t('workOrder.markCompletedSuccess', 'Work Order marked as completed'),
        severity: 'success'
      });

      handleCloseMarkTask();
    } catch (err) {
      console.error('mark task save failed', err);
      notify({ message: t('workOrder.failedSaveChanges', 'Failed to save changes'), severity: 'error' });
    } finally {
      setMarkSubmitting(false);
    }
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setEditingId(null);
  };

  const handleDialogSuccess = (msg, newStatus) => {
    notify({ message: msg || t('serviceType.savedSuccess', 'Saved successfully'), severity: 'success' });
    if (newStatus && typeof newStatus === 'string') {
      setStatusFilter(newStatus);
    }
    revalidateVehicleMaintenanceList();

    handleDialogClose();
    setPaginationModel((p) => ({ ...p, page: 0 }));
  };

  const handleDialogError = (msg) => {
    notify({ message: msg || t('serviceType.operationFailed', 'Operation failed'), severity: 'error' });
  };

  const formatCurrency = (v) =>
    new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(Number(v || 0));
  const fmtDateID = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    if (isNaN(d)) return '-';
    return d.toLocaleDateString('id-ID', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const columns = [
    {
      field: 'actions',
      headerName: t('general.actions', 'Actions'),
      sortable: false,
      flex: 0.8,
      pinned: 'left',
      renderCell: (params) => {
        const row = params.row || {};
        const st = row.status || '';
        if (st === 'completed') {
          return (
            <IconButton size="small" onClick={() => handleShowDetailWO(row)} title={t('workOrder.viewDetail', 'View detail')}>
              <VisibilityIcon fontSize="small" />
            </IconButton>
          );
        }
        return (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton size="small" onClick={() => handleEditClick(row.id)} title={t('buttonTitle.edit', 'Edit')}>
              <EditIcon fontSize="small" color="primary" />
            </IconButton>
            <IconButton size="small" onClick={() => handleMarkTask(row.id)} title={t('workOrder.markCompleted', 'Mark Completed')}>
              <TaskAltOutlinedIcon fontSize="small" color="info" />
            </IconButton>
          </Box>
        );
      }
    },
    { field: 'vm_number', headerName: t('workOrder.woNumber', 'WO Number'), flex: 1, pinned: 'left' },
    { field: 'license_plate', headerName: t('workOrder.vehicle', 'Vehicle'), flex: 1 },
    {
      field: 'vm_date',
      headerName: t('workOrder.woDate', 'WO Date'),
      flex: 1,
      valueGetter: (value, row) => fmtDateID(row.vm_date)
    },
    {
      field: statusFilter == 'onprogress' ? 'date_completed' : 'date_completed_actual',
      headerName: statusFilter == 'onprogress' ? t('workOrder.estimateDate', 'Estimate Date') : t('workOrder.actualDate', 'Actual Date'),
      flex: 1,
      valueGetter: (_, row) => {
        if (statusFilter == 'onprogress') {
          return fmtDateID(row.date_completed);
        }
        return fmtDateID(row.date_completed_actual);
      }
    },
    {
      field: 'workshop',
      headerName: t('workOrder.workshop', 'Workshop'),
      flex: 1,
      valueGetter: (value, row) => row?.join?.workshop_name || '-'
    },
    {
      field: 'status',
      headerName: t('workOrder.status', 'Status'),
      flex: 0.8,
      renderCell: (p) =>
        p.row?.status ? (
          <Chip
            label={
              p.row.status === 'onprogress' ? t('workOrder.statusOnProgress', 'ON PROGRESS') : t('workOrder.statusCompleted', 'COMPLETED')
            }
            size="small"
            color={p.row.status === 'onprogress' ? 'primary' : 'success'}
            sx={{ fontSize: '10px', height: '20px', '& .MuiChip-label': { padding: '0 6px', fontSize: '10px' } }}
          />
        ) : (
          '-'
        )
    },
    {
      field: 'price',
      headerName: t('workOrder.total', 'Total'),
      flex: 1,
      valueGetter: (value, row) => formatCurrency(row.price)
    },
    {
      field: 'service_type',
      headerName: t('workOrder.serviceType', 'Service Type'),
      sortable: false,
      flex: 1,
      renderCell: (p) => (
        <Button onClick={() => handleShowDetail(p.row)}>
          <AssignmentIcon color="info" />
        </Button>
      )
    }
  ];

  return (
    <Box>
      <DataTable
        title={t('workOrder.title', 'Work Orders')}
        rows={rows}
        columns={columns}
        loading={isLoading}
        pageSizeOptions={[10, 25, 50]}
        paginationMode="server"
        paginationModel={paginationModel}
        rowCount={totalRecords}
        onPaginationModelChange={handlePageChange}
        searchQuery={searchRaw}
        showStatusFilter={true}
        initialStatus={statusFilter || ''}
        statusOptions={[
          { value: 'onprogress', label: t('workOrder.onProgress', 'On Progress') },
          { value: 'completed', label: t('workOrder.completed', 'Completed') }
        ]}
        onStatusChange={(v) => {
          setStatusFilter(v || '');
          setPaginationModel((p) => ({ ...p, page: 0 }));
        }}
        onSearch={({ field, value }) => handleSearch({ field, value })}
        onSearchChange={(e) => {
          setSearchRaw(e?.target?.value ?? '');
        }}
        searchFieldOptions={searchFieldOptions}
        selectedSearchField={searchField}
        onSearchFieldChange={(f) => {
          setSearchField(f);
          setSearchRaw('');
          setSearchValue('');
          setPaginationModel((p) => ({ ...p, page: 0 }));
        }}
        onAddClick={handleAddClick}
        companyOptions={companyOptionsNormalized}
        selectedCompanies={selectedCompanies}
        onCompanyChange={handleCompanyChange}
        onFetchAllForExport={handleFetchAllForExport}
        onExportExcel={async (data) => {
          const exportData = data && data.length > 0 ? data : await handleFetchAllForExport();
          exportExcelWorkOrder(exportData, t);
        }}
      />

      <FormDialog
        open={openDialogMarkTask}
        onClose={handleCloseMarkTask}
        title={t('workOrder.markWoCompletedTitle', 'Mark Work Order Completed')}
        fullScreenMode={false}
        fullHeight={false}
        actions={
          <>
            <Button onClick={handleCloseMarkTask} sx={{ textTransform: 'none' }}>
              {t('buttonTitle.cancel', 'Cancel')}
            </Button>
            <Button
              variant="contained"
              disabled={markSubmitting}
              onClick={handleSaveMarkTask}
              sx={{ color: 'primary', textTransform: 'none' }}
            >
              {markSubmitting ? t('buttonTitle.processing', 'Processing...') : t('buttonTitle.save', 'Save')}
            </Button>
          </>
        }
      >
        <Box sx={{ width: '100%', px: 2, py: 2 }}>
          {markLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label={t('workOrder.actualOdo', 'Actual Odo')}
                value={markValues.actual_odometer ?? ''}
                onChange={(e) => setMarkValues((s) => ({ ...s, actual_odometer: e.target.value }))}
                fullWidth
              />
              <DatePicker
                format="DD-MM-YYYY"
                label={t('workOrder.actualCompletionDate', 'Actual Completion Date')}
                value={
                  markValues.date_completed_actual
                    ? dayjs.isDayjs(markValues.date_completed_actual)
                      ? markValues.date_completed_actual
                      : dayjs(markValues.date_completed_actual)
                    : dayjs()
                }
                onChange={(newValue) => setMarkValues((s) => ({ ...s, date_completed_actual: newValue }))}
                slotProps={{
                  textField: { size: 'small', fullWidth: true }
                }}
              />
            </Box>
          )}
        </Box>
      </FormDialog>

      <WorkOrderAddEdit
        open={openDialog}
        ref={formRef}
        id={editingId}
        onClose={handleDialogClose}
        onSuccess={(msg, status) => handleDialogSuccess(msg, status)}
        onError={(msg) => handleDialogError(msg)}
        //   onSubmitStart={handleDialogSubmitStart}
        //   onSubmitEnd={handleDialogSubmitEnd}
      />

      {/* <DeleteConfirmDialog
        open={confirmOpen}
        loading={confirmLoading}
        onClose={handleConfirmClose}
        onConfirm={handleConfirmDelete}
        title="Confirm Delete"
        message="Are you sure you want to delete this item?"
        confirmLabel="Delete"
        cancelLabel="Cancel"
      /> */}

      <DetailServiceTypeDialog open={openDialogDetail} onClose={() => setOpenDialogDetail(false)} data={detailRow} />
      <DetailWorkOrderDialog open={openDialogDetailWO} onClose={() => setOpenDialogDetailWO(false)} data={detailWORow} />
    </Box>
  );
}
